import { useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { supabase } from '../supabaseClient';
import { openCustomerPortal, STRIPE_CONFIGURED } from '../lib/stripe';
import { redirectToSignedOutSignIn } from '../lib/navigation';
import { BILLING_UNAVAILABLE_MESSAGE, PASSWORD_MIN_LENGTH, SUBSCRIPTION_LOOKUP_FAILED_MESSAGE } from '../lib/userMessages';
import { canUseInternalAdmin } from '../lib/internalUsers';
import { recordRecoveryAction, refreshAppShell } from '../lib/appRecovery';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { showToast } from '../lib/toast';
import { resetSupabaseCircuit } from '../lib/supabaseCircuitBreaker';
import { Sentry } from '../lib/sentry';
import AdminDiagnosticsPanel from './AdminDiagnosticsPanel';
import { useIsMobile } from '../hooks/useIsMobile';

const ToggleSwitch = ({ on, onToggle, theme, ariaLabel, disabled = false }) => (
  <button
    onClick={disabled ? undefined : onToggle}
    aria-pressed={on}
    aria-label={ariaLabel}
    disabled={disabled}
    style={{ width:48,height:26,borderRadius:13,border:"none",background:on?theme.toggleActive:theme.toggle,position:"relative",cursor:disabled?"not-allowed":"pointer",transition:"background 0.25s ease",flexShrink:0,opacity:disabled?0.52:1 }}
  >
    <span style={{ position:"absolute",top:3,left:on?25:3,width:20,height:20,borderRadius:"50%",background:theme.toggleKnob,transition:"left 0.25s ease",boxShadow:"0 1px 3px rgba(0,0,0,0.3)" }} />
  </button>
);

const SectionHeader = ({ title, theme }) => (
  <div style={{ marginBottom:18,paddingBottom:10,borderBottom:`1px solid ${theme.border}` }}>
    <h2 style={{ margin:0,fontFamily:"'DM Sans', sans-serif",fontSize:13,color:theme.accent,fontWeight:800,letterSpacing:0,textTransform:"uppercase" }}>{title}</h2>
  </div>
);

const SettingRow = ({ label, description, children, theme }) => (
  <div style={{ display:"flex",flexWrap:"wrap",justifyContent:"space-between",alignItems:"center",gap:14,padding:"14px 0",borderBottom:`1px solid ${theme.border}` }}>
    <div style={{ flex:"1 1 160px",minWidth:0 }}>
      <div style={{ fontFamily:"'DM Sans', sans-serif",fontSize:"var(--text-base)",fontWeight:650,color:theme.text,marginBottom:description?3:0 }}>{label}</div>
      {description && <div style={{ fontFamily:"'DM Sans', sans-serif",fontSize:"var(--text-sm)",color:theme.textMuted,lineHeight:1.35 }}>{description}</div>}
    </div>
    {children}
  </div>
);

const ActionButton = ({ label, onClick, variant = "outline", theme, disabled = false }) => {
  const [hovered, setHovered] = useState(false);
  const isFilled = variant === "filled";
  return (
    <button disabled={disabled} onClick={disabled ? undefined : onClick} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} style={{ padding:"11px 18px",borderRadius:8,border:isFilled?"none":`1.5px solid ${theme.accent}`,background:isFilled?(hovered&&!disabled?theme.accentHover:theme.accent):(hovered&&!disabled?theme.accent:"transparent"),color:isFilled?theme.textDark:(hovered&&!disabled?theme.textDark:theme.accent),fontFamily:"'DM Sans', sans-serif",fontSize:"var(--text-base)",fontWeight:700,cursor:disabled?"not-allowed":"pointer",transition:"all 0.2s ease",letterSpacing:0,opacity:disabled?0.55:1 }}>
      {label}
    </button>
  );
};

const SelectDropdown = ({ value, options, onChange, theme }) => (
  <select value={value} onChange={e => onChange(e.target.value)} style={{ background:theme.bgCard,color:theme.text,border:`1px solid ${theme.border}`,borderRadius:8,padding:"7px 10px",fontFamily:"'DM Sans', sans-serif",fontSize:"var(--text-input, 16px)",fontWeight:650,cursor:"pointer",outline:"none" }}>
    {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
  </select>
);

const REFERRAL_LOOKUP_TIMEOUT_MS = 8000;

function withReferralTimeout(promise) {
  let timeoutId;
  const timeout = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error('Referral lookup timed out')), REFERRAL_LOOKUP_TIMEOUT_MS);
  });
  return Promise.race([Promise.resolve(promise), timeout]).finally(() => clearTimeout(timeoutId));
}

export default function SettingsPanel({ onClose, prefs, savePref, setDashPeriod, onUpgradeClick, isDesktopLayout }) {
  const { theme, isDark, toggleTheme } = useTheme();
  const { user, signOut } = useAuth();
  const { isPro, error: subscriptionError, refresh: refreshSubscription } = useSubscription();
  const { status: pushStatus, enable: enablePush, disable: disablePush, sendTest: sendPushTest } = usePushNotifications(user?.id);
  const [portalLoading, setPortalLoading] = useState(false);
  const isNarrow = useIsMobile(900);
  const isDesktop = typeof isDesktopLayout === 'boolean' ? isDesktopLayout : !isNarrow;

  const handlePushToggle = async () => {
    if (pushStatus === 'on') {
      const ok = await disablePush();
      showToast(ok ? 'Push alerts disabled on this device' : 'Could not disable push alerts');
    } else if (pushStatus === 'off') {
      const ok = await enablePush();
      showToast(ok ? 'Push alerts enabled on this device' : 'Could not enable push alerts — check notification permissions');
    }
  };

  const handlePushTest = async () => {
    const ok = await sendPushTest();
    showToast(ok ? 'Test notification sent' : 'Test notification failed — check device settings');
  };
  const [billingMessage, setBillingMessage] = useState("");
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ password: "", confirm: "" });
  const [passwordStatus, setPasswordStatus] = useState({ type: "", text: "" });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [referral, setReferral] = useState(null);
  const [referralLoading, setReferralLoading] = useState(false);
  const [referralMessage, setReferralMessage] = useState("");
  const [referralFailed, setReferralFailed] = useState(false);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [recoveryMessage, setRecoveryMessage] = useState("");
  const referralLoadedForUserRef = useRef(null);
  const referralLoadIdRef = useRef(0);

  useEffect(() => {
    if (!portalLoading) return undefined;

    const resetPortalLoading = () => setPortalLoading(false);
    const resetWhenVisible = () => {
      if (document.visibilityState === 'visible') resetPortalLoading();
    };

    window.addEventListener('pageshow', resetPortalLoading);
    window.addEventListener('focus', resetPortalLoading);
    document.addEventListener('visibilitychange', resetWhenVisible);

    return () => {
      window.removeEventListener('pageshow', resetPortalLoading);
      window.removeEventListener('focus', resetPortalLoading);
      document.removeEventListener('visibilitychange', resetWhenVisible);
    };
  }, [portalLoading]);

  const loadReferral = useCallback(({ force = false } = {}) => {
    const userId = user?.id;
    if (!userId) return undefined;
    if (!force && referralLoadedForUserRef.current === userId) return undefined;

    const loadId = referralLoadIdRef.current + 1;
    referralLoadIdRef.current = loadId;
    let cancelled = false;
    setReferralLoading(true);
    setReferralFailed(false);
    setReferralMessage("");
    withReferralTimeout(supabase.rpc('get_own_referral_details'))
      .then(({ data, error }) => {
        if (cancelled || referralLoadIdRef.current !== loadId) return;
        if (error) throw error;
        setReferral(data || null);
        referralLoadedForUserRef.current = userId;
      })
      .catch(err => {
        if (cancelled || referralLoadIdRef.current !== loadId) return;
        console.warn('[SiteScope] Referral details failed:', err.message || err);
        Sentry.captureException(err, {
          tags: { sitescope_area: 'settings', sitescope_action: 'load_referral' },
        });
        setReferralFailed(true);
        setReferralMessage('Referral code could not load. Try again in a moment.');
      })
      .finally(() => {
        if (!cancelled && referralLoadIdRef.current === loadId) setReferralLoading(false);
      });
    return () => { cancelled = true; };
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) {
      referralLoadedForUserRef.current = null;
      setReferral(null);
      setReferralLoading(false);
      setReferralFailed(false);
      setReferralMessage("");
      return undefined;
    }
    return loadReferral();
  }, [user?.id, loadReferral]);

  const handleToggleDarkMode = () => {
    const next = !isDark;
    toggleTheme();
    savePref('dark_mode', next);
  };

  const handleSetDashPeriod = (v) => {
    setDashPeriod(parseInt(v, 10));
  };

  const handleSetDefaultMap = (v) => {
    savePref('default_map_view', v);
  };

  const handleSignOut = async () => {
    onClose();
    try {
      await signOut();
    } catch (err) {
      console.error('[SiteScope] Sign-out error:', err);
    } finally {
      redirectToSignedOutSignIn();
    }
  };

  const handleManageBilling = async () => {
    setPortalLoading(true);
    setBillingMessage("");
    try {
      await openCustomerPortal();
    } catch (err) {
      console.error('[SiteScope] Portal error:', err.message);
      setBillingMessage(BILLING_UNAVAILABLE_MESSAGE);
      setPortalLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordStatus({ type: "", text: "" });
    if (passwordForm.password.length < PASSWORD_MIN_LENGTH) {
      setPasswordStatus({ type: "error", text: `Password must be at least ${PASSWORD_MIN_LENGTH} characters.` });
      return;
    }
    if (passwordForm.password !== passwordForm.confirm) {
      setPasswordStatus({ type: "error", text: "Passwords don't match." });
      return;
    }
    setPasswordLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: passwordForm.password });
      if (error) throw error;
      setPasswordForm({ password: "", confirm: "" });
      setShowPasswordForm(false);
      setPasswordStatus({ type: "success", text: "Password updated." });
    } catch (err) {
      setPasswordStatus({ type: "error", text: err.message || "Unable to update password." });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleFeatureRequest = () => { window.location.href = "mailto:features@sitescopeapp.com?subject=SiteScope%20Feature%20Request&body=Hi%20SiteScope%20team%2C%0D%0A%0D%0AI'd%20like%20to%20suggest%20the%20following%20feature%3A%0D%0A%0D%0A"; };
  const handleContactUs = () => { window.location.href = "mailto:support@sitescopeapp.com?subject=SiteScope%20Support&body=Hi%20SiteScope%20team%2C%0D%0A%0D%0A"; };
  const handleResetConnection = () => {
    resetSupabaseCircuit();
    recordRecoveryAction('reset_supabase_circuit', { source: 'settings' });
    setRecoveryMessage("Connection reset on this device. Try the app again now.");
  };
  const handleFullDeviceReset = () => {
    window.location.assign('/reset');
  };
  const referralCode = referral?.code || '';
  const referralUrl = referralCode ? `${window.location.origin}/?auth=signup&code=${encodeURIComponent(referralCode)}` : '';
  const handleCopyReferral = async () => {
    if (!referralCode && referralFailed) {
      loadReferral({ force: true });
      return;
    }
    if (!referralUrl) return;
    try {
      await navigator.clipboard.writeText(referralUrl);
      setReferralMessage("Referral link copied.");
    } catch {
      setReferralMessage(`Code: ${referralCode}`);
    }
  };

  return (
    <>
    <div
      onClick={isDesktop ? onClose : undefined}
      style={{
        position:"fixed",
        top:0,
        right:0,
        bottom:0,
        left:isDesktop ? 0 : 0,
        zIndex:1000,
        background:isDesktop ? "rgba(2,8,18,0.72)" : theme.bg,
        overflowY:isDesktop ? "hidden" : "auto",
        transition:"background 0.3s ease",
        display:isDesktop ? "flex" : "block",
        justifyContent:isDesktop ? "flex-end" : undefined,
        backdropFilter:isDesktop ? "blur(2px)" : "none",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width:isDesktop ? "min(40vw, 560px)" : "100%",
          minWidth:isDesktop ? 420 : undefined,
          maxWidth:isDesktop ? 560 : undefined,
          height:"100%",
          background:theme.bg,
          overflowY:"auto",
          boxShadow:isDesktop ? "-24px 0 70px rgba(0,0,0,0.42)" : "none",
          borderLeft:isDesktop ? `1px solid ${theme.border}` : "none",
        }}
      >
      {/* Header */}
      <div style={{ position:"sticky",top:0,zIndex:10,display:"flex",alignItems:"center",justifyContent:"space-between",padding:"16px 20px",background:theme.bgPanel,borderBottom:`1px solid ${theme.border}`,backdropFilter:"blur(10px)" }}>
        <div style={{ width:40 }} />
        <h1 style={{ margin:0,fontFamily:"'DM Sans', sans-serif",fontSize:"var(--text-xl)",color:theme.text,fontWeight:800,letterSpacing:0 }}>Settings</h1>
        <button onClick={onClose} style={{ width:36,height:36,borderRadius:8,border:`1px solid ${theme.border}`,background:"transparent",color:theme.textMuted,fontSize:18,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",transition:"all 0.2s" }} aria-label="Close settings">✕</button>
      </div>

      <div style={{ maxWidth:520,margin:"0 auto",padding:"24px 20px 100px" }}>

        {/* Appearance */}
        <div style={{ background:theme.bgCard,borderRadius:8,padding:"20px",marginBottom:16,border:`1px solid ${theme.border}` }}>
          <SectionHeader title="Appearance" theme={theme} />
          <SettingRow label="Dark Mode" description="Switch between dark and light theme" theme={theme}>
            <ToggleSwitch on={isDark} onToggle={handleToggleDarkMode} theme={theme} ariaLabel="Toggle dark mode" />
          </SettingRow>
          <SettingRow label="Text Size" description="Adjust text size for readability" theme={theme}>
            <div style={{ display:"flex", gap:"4px" }}>
              {[
                { value:'small',  a:'11px', caption:'Small'  },
                { value:'medium', a:'14px', caption:'Med'    },
                { value:'large',  a:'17px', caption:'Large'  },
                { value:'xlarge', a:'20px', caption:'XL'     },
              ].map(({ value, a, caption }) => {
                const active = (prefs.text_size || 'medium') === value;
                return (
                  <button
                    key={value}
                    onClick={() => {
                      savePref('text_size', value);
                      document.documentElement.setAttribute('data-text-size', value);
                    }}
                    aria-label={`Text size ${caption}`}
                    aria-pressed={active}
                    style={{
                      width:'46px', height:'48px', borderRadius:'8px',
                      border:`1.5px solid ${active ? theme.accent : theme.border}`,
                      background: active ? `${theme.accent}26` : theme.bgCard,
                      color: active ? theme.accent : theme.textMuted,
                      cursor:'pointer', display:'flex', flexDirection:'column',
                      alignItems:'center', justifyContent:'center', gap:'3px',
                      transition:'all 0.15s', padding:0,
                    }}
                  >
                    <span style={{ fontSize:a, fontFamily:'Georgia, serif', fontWeight:'bold', lineHeight:1 }}>A</span>
                    <span style={{ fontSize:'8px', fontFamily:"'DM Sans',sans-serif", letterSpacing:'0.3px' }}>{caption}</span>
                  </button>
                );
              })}
            </div>
          </SettingRow>
          <SettingRow label="Help Tips" description="Show helpful tips on each screen" theme={theme}>
            <ToggleSwitch on={prefs.show_help_tips !== false} onToggle={() => savePref('show_help_tips', !(prefs.show_help_tips !== false))} theme={theme} ariaLabel="Toggle help tips" />
          </SettingRow>
        </div>

        {/* Notifications */}
        <div style={{ background:theme.bgCard,borderRadius:8,padding:"20px",marginBottom:16,border:`1px solid ${theme.border}` }}>
          <SectionHeader title="Notifications" theme={theme} />
          <SettingRow label="Email digest" description="Choose how often to receive public DA activity for your selected councils and watch zone" theme={theme}>
            <SelectDropdown
              value={prefs.email_digest === false || prefs.digest_frequency === 'off' ? 'off' : prefs.digest_frequency === 'daily' || prefs.digest_frequency === 'morning' ? 'daily' : 'weekly'}
              onChange={value => {
                savePref('digest_frequency', value === 'daily' ? 'daily' : value);
                savePref('email_digest', value !== 'off');
              }}
              theme={theme}
              options={isPro
                ? [{value:'weekly',label:'Weekly'},{value:'daily',label:'Daily morning'},{value:'off',label:'Off'}]
                : [{value:'weekly',label:'Weekly'},{value:'off',label:'Off'}]}
            />
          </SettingRow>
          <SettingRow
            label="Install App & Enable Push"
            description={
              pushStatus === 'install_required'
                ? 'On iPhone or iPad: Share → Add to Home Screen, open the installed app, then enable push here.'
                : pushStatus === 'unsupported'
                  ? 'Install SiteScope from a supported browser to use device notifications.'
                  : pushStatus === 'denied'
                    ? 'Notifications are blocked for SiteScope in your device settings.'
                    : isPro
                      ? 'Install SiteScope for the 7am briefing ritual and enable Pro instant alerts on this device.'
                      : 'Install SiteScope for the 7am ritual. Device setup is available now; instant DA alerts remain Pro-only.'
            }
            theme={theme}
          >
            {['on', 'off', 'busy'].includes(pushStatus) ? (
              <ToggleSwitch
                on={pushStatus === 'on'}
                disabled={pushStatus === 'busy'}
                onToggle={handlePushToggle}
                theme={theme}
                ariaLabel="Toggle device push setup"
              />
            ) : (
              <span style={{ fontFamily:"'DM Sans', sans-serif",fontSize:"var(--text-xs)",fontWeight:750,color:theme.textMuted,border:`1px solid ${theme.border}`,borderRadius:999,padding:"5px 9px",whiteSpace:"nowrap" }}>
                {pushStatus === 'install_required' ? 'Install app' : pushStatus === 'denied' ? 'Blocked' : 'Unavailable'}
              </span>
            )}
          </SettingRow>
          {isPro && pushStatus === 'on' && (
            <SettingRow label="Test Notification" description="Send a test push to this device" theme={theme}>
              <ActionButton label="Send test" onClick={handlePushTest} theme={theme} />
            </SettingRow>
          )}
          {isPro ? (
            <>
              <SettingRow label="Watch Zone Alerts" description="Notifications when new DAs appear in your watch zones" theme={theme}>
                <ToggleSwitch on={prefs.watch_zone_alerts} onToggle={() => savePref('watch_zone_alerts', !prefs.watch_zone_alerts)} theme={theme} ariaLabel="Toggle watch zone alerts" />
              </SettingRow>
              <SettingRow label="DA Status Change Alerts" description="Email me when a tracked DA changes status" theme={theme}>
                <ToggleSwitch on={prefs.notification_das} onToggle={() => savePref('notification_das', !prefs.notification_das)} theme={theme} ariaLabel="Toggle DA status change alerts" />
              </SettingRow>
              <SettingRow label="Weekly Watch Zone Digest" description="Weekly app summary of DA activity within your watch zones" theme={theme}>
                <ToggleSwitch on={prefs.notification_zones} onToggle={() => savePref('notification_zones', !prefs.notification_zones)} theme={theme} ariaLabel="Toggle weekly watch zone digest" />
              </SettingRow>
            </>
          ) : (
            <SettingRow label="Pro Instant Alerts" description="Watch-zone, tracked-status and exhibition alerts remain available only on Pro plans." theme={theme}>
              <ActionButton label="Upgrade" onClick={onUpgradeClick} variant="filled" theme={theme} />
            </SettingRow>
          )}
        </div>

        {/* App Preferences */}
        <div style={{ background:theme.bgCard,borderRadius:8,padding:"20px",marginBottom:16,border:`1px solid ${theme.border}` }}>
          <SectionHeader title="App Preferences" theme={theme} />
          <SettingRow label="Default Map View" description="Centre the map on this area when you open the Map tab" theme={theme}>
            <SelectDropdown value={prefs.default_map_view} onChange={handleSetDefaultMap} theme={theme} options={[{value:"byron",label:"Byron Shire"},{value:"ballina",label:"Ballina Shire"},{value:"lismore",label:"Lismore City"},{value:"tweed",label:"Tweed Shire"},{value:"all",label:"All Northern Rivers"}]} />
          </SettingRow>
          <SettingRow label="Dashboard Period" description="Filter summary counts and trends by this period" theme={theme}>
            <SelectDropdown value={String(Number.isFinite(Number(prefs.dashboard_period)) ? Number(prefs.dashboard_period) : 30)} onChange={handleSetDashPeriod} theme={theme} options={[{value:"7",label:"7 days"},{value:"30",label:"30 days"},{value:"90",label:"90 days"},{value:"180",label:"180 days"},{value:"0",label:"Last 365 days"}]} />
          </SettingRow>
        </div>

        {/* Account */}
        <div style={{ background:theme.bgCard,borderRadius:8,padding:"20px",marginBottom:16,border:`1px solid ${theme.border}` }}>
          <SectionHeader title="Account" theme={theme} />
          {user && (
            <SettingRow label="Signed in as" description={user.user_metadata?.full_name || ""} theme={theme}>
              <span style={{ fontFamily:"'DM Sans', sans-serif",fontSize:"var(--text-sm)",color:theme.textMuted,maxWidth:160,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{user.email}</span>
            </SettingRow>
          )}
          <SettingRow label="Subscription" description={!STRIPE_CONFIGURED ? "Online billing is not enabled. For account help, contact support@sitescopeapp.com." : isPro ? "Manage your plan, billing and payment details" : "Upgrade to Pro for supply chain intelligence"} theme={theme}>
            {!STRIPE_CONFIGURED ? (
              <span style={{ color: theme.textMuted }}>Billing not enabled</span>
            ) : isPro ? (
              <ActionButton
                label={portalLoading ? "Opening…" : "Manage Billing"}
                onClick={handleManageBilling}
                variant="outline"
                theme={theme}
              />
            ) : (
              <ActionButton
                label="Upgrade to Pro"
                onClick={() => { onClose(); onUpgradeClick && onUpgradeClick(); }}
                variant="filled"
                theme={theme}
              />
            )}
          </SettingRow>
          <SettingRow
            label="Referral"
            description={STRIPE_CONFIGURED ? "When someone joins with your code and becomes a paid subscriber, your next month is 50% off." : "Referral rewards require paid subscriptions. Online billing is currently unavailable."}
            theme={theme}
          >
            <ActionButton
              label={referralLoading ? "Loading..." : referralCode ? "Copy Link" : referralFailed ? "Retry" : "Unavailable"}
              onClick={handleCopyReferral}
              variant="outline"
              theme={theme}
              disabled={referralLoading || (!referralCode && !referralFailed)}
            />
          </SettingRow>
          {(referralCode || referralMessage) && (
            <div style={{ margin:"-4px 0 12px",padding:"10px 12px",background:theme.bgPanel,border:`1px solid ${theme.border}`,borderRadius:8,fontFamily:"'DM Sans', sans-serif",fontSize:"var(--text-sm)",color:theme.textMuted,lineHeight:1.45 }}>
              {referralCode && <div style={{ color:theme.text,fontWeight:800,letterSpacing:"0.8px",marginBottom:3 }}>{referralCode}</div>}
              {referralCode && <div>{referral?.applied_count || 0} reward{Number(referral?.applied_count || 0) === 1 ? '' : 's'} applied</div>}
              {referralMessage && <div style={{ color:theme.accent,marginTop:4 }}>{referralMessage}</div>}
            </div>
          )}
          {subscriptionError && (
            <div style={{ margin:"0 0 12px",padding:"10px 12px",background:"rgba(212,168,74,0.1)",border:"1px solid rgba(212,168,74,0.3)",borderRadius:8,fontFamily:"'DM Sans', sans-serif",fontSize:"var(--text-sm)",color:theme.accent,lineHeight:1.45 }}>
              {SUBSCRIPTION_LOOKUP_FAILED_MESSAGE}
              <button
                type="button"
                onClick={refreshSubscription}
                style={{ marginLeft:8,background:"transparent",border:"none",color:theme.accent,fontFamily:"'DM Sans', sans-serif",fontSize:"var(--text-sm)",fontWeight:800,cursor:"pointer",textDecoration:"underline",padding:0 }}
              >
                Refresh
              </button>
            </div>
          )}
          {billingMessage && (
            <div style={{ margin:"0 0 12px",padding:"10px 12px",background:"rgba(212,168,74,0.1)",border:"1px solid rgba(212,168,74,0.3)",borderRadius:8,fontFamily:"'DM Sans', sans-serif",fontSize:"var(--text-sm)",color:theme.accent,lineHeight:1.45 }}>
              {billingMessage}
            </div>
          )}
          <SettingRow label="Password" description="Update the password for this account" theme={theme}>
            <ActionButton
              label={showPasswordForm ? "Cancel" : "Change Password"}
              onClick={() => {
                setShowPasswordForm(p => !p);
                setPasswordStatus({ type: "", text: "" });
                setPasswordForm({ password: "", confirm: "" });
              }}
              variant="outline"
              theme={theme}
            />
          </SettingRow>
          {showPasswordForm && (
            <form onSubmit={handlePasswordChange} style={{ borderBottom:`1px solid ${theme.border}`,padding:"4px 0 16px",display:"grid",gap:10 }}>
              <input
                type="password"
                value={passwordForm.password}
                onChange={e => setPasswordForm(p => ({ ...p, password: e.target.value }))}
                placeholder="New password"
                autoComplete="new-password"
                style={{ background:theme.bgPanel,color:theme.text,border:`1px solid ${theme.border}`,borderRadius:8,padding:"11px 12px",fontFamily:"'DM Sans', sans-serif",fontSize:"var(--text-input, 16px)",outline:"none",boxSizing:"border-box" }}
              />
              <input
                type="password"
                value={passwordForm.confirm}
                onChange={e => setPasswordForm(p => ({ ...p, confirm: e.target.value }))}
                placeholder="Confirm new password"
                autoComplete="new-password"
                style={{ background:theme.bgPanel,color:theme.text,border:`1px solid ${theme.border}`,borderRadius:8,padding:"11px 12px",fontFamily:"'DM Sans', sans-serif",fontSize:"var(--text-input, 16px)",outline:"none",boxSizing:"border-box" }}
              />
              <div style={{ display:"flex",alignItems:"center",gap:12,justifyContent:"space-between",flexWrap:"wrap" }}>
                <span style={{ fontFamily:"'DM Sans', sans-serif",fontSize:"var(--text-sm)",color:passwordStatus.type==="error"?theme.danger:theme.textMuted }}>
                  {passwordStatus.text || `Minimum ${PASSWORD_MIN_LENGTH} characters.`}
                </span>
                <ActionButton
                  label={passwordLoading ? "Updating..." : "Save Password"}
                  type="submit"
                  variant="filled"
                  theme={theme}
                  disabled={passwordLoading}
                />
              </div>
            </form>
          )}
          {!showPasswordForm && passwordStatus.text && (
            <div style={{ fontFamily:"'DM Sans', sans-serif",fontSize:"var(--text-sm)",color:passwordStatus.type==="error"?theme.danger:theme.textMuted,padding:"8px 0 0" }}>
              {passwordStatus.text}
            </div>
          )}
          {canUseInternalAdmin(user?.email) && (
            <SettingRow label="Diagnostics" description="App health information for support" theme={theme}>
              <ActionButton
                label="Open"
                onClick={() => setShowDiagnostics(true)}
                variant="outline"
                theme={theme}
              />
            </SettingRow>
          )}
          <div style={{ paddingTop:16 }}>
            <button
              onClick={handleSignOut}
              style={{ background:"transparent", border:"none", color:theme.danger, fontSize:"var(--text-sm)", cursor:"pointer", padding:"8px 0", fontFamily:"'DM Sans', sans-serif",fontWeight:700 }}
            >
              Sign Out
            </button>
          </div>
        </div>

        {/* Device Recovery */}
        <div style={{ background:theme.bgCard,borderRadius:8,padding:"20px",marginBottom:16,border:`1px solid ${theme.border}` }}>
          <SectionHeader title="Device Recovery" theme={theme} />
          <p style={{ fontFamily:"'DM Sans', sans-serif",fontSize:"var(--text-base)",color:theme.textMuted,margin:"0 0 12px",lineHeight:1.5 }}>
            Use these if SiteScope looks stale, will not load, or feels stuck on this device. They only affect this browser.
          </p>
          <SettingRow label="Reset connection" description="Retries the connection without signing you out" theme={theme}>
            <ActionButton
              label="Reset"
              onClick={handleResetConnection}
              variant="outline"
              theme={theme}
            />
          </SettingRow>
          <SettingRow label="Refresh app files" description="Refreshes cached app files and reloads SiteScope" theme={theme}>
            <ActionButton
              label="Repair"
              onClick={refreshAppShell}
              variant="outline"
              theme={theme}
            />
          </SettingRow>
          <SettingRow label="Full device reset" description="Clears SiteScope cache and local app state on this device" theme={theme}>
            <ActionButton
              label="Open Reset"
              onClick={handleFullDeviceReset}
              variant="outline"
              theme={theme}
            />
          </SettingRow>
          {recoveryMessage && (
            <div style={{ fontFamily:"'DM Sans', sans-serif",fontSize:"var(--text-sm)",color:theme.accent,padding:"8px 0 0",lineHeight:1.4 }}>
              {recoveryMessage}
            </div>
          )}
        </div>

        {/* Support */}
        <div style={{ background:theme.bgCard,borderRadius:8,padding:"20px",marginBottom:16,border:`1px solid ${theme.border}` }}>
          <SectionHeader title="Support" theme={theme} />
          <p style={{ fontFamily:"'DM Sans', sans-serif",fontSize:"var(--text-base)",color:theme.textMuted,margin:"0 0 16px",lineHeight:1.5 }}>Have an idea or need help? We'd love to hear from you.</p>
          <div style={{ display:"flex",gap:12,flexWrap:"wrap" }}>
            <ActionButton label="Request a Feature" onClick={handleFeatureRequest} variant="outline" theme={theme} />
            <ActionButton label="Contact Us" onClick={handleContactUs} variant="filled" theme={theme} />
          </div>
        </div>

        {/* Footer */}
        <div style={{ textAlign:"center",padding:"24px 0 16px" }}>
          <div style={{ display:"inline-flex",alignItems:"center",gap:8,marginBottom:8 }}>
            <div style={{ width:24,height:24,borderRadius:"18.5%",background:"linear-gradient(145deg, #c0a060, #8a7040, #a89050)",border:"1.5px solid #c0a060",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Sans', sans-serif",fontSize:14,fontWeight:700,color:"#0b0f1a" }}>S</div>
            <span style={{ fontFamily:"'DM Sans', sans-serif",fontSize:14,fontWeight:800,color:theme.textMuted }}>SiteScope</span>
          </div>
          <div style={{ fontFamily:"'DM Sans', sans-serif",fontSize:"var(--text-xs)",color:theme.textMuted,marginBottom:8 }}>DA Tracking & Alerts — v1.7</div>
          <div style={{ display:"flex",gap:16,justifyContent:"center",marginBottom:8 }}>
            <a href="/privacy" style={{ fontFamily:"'DM Sans', sans-serif",fontSize:"var(--text-xs)",color:theme.textMuted,textDecoration:"none",opacity:0.7 }}>Privacy Policy</a>
            <a href="/terms"   style={{ fontFamily:"'DM Sans', sans-serif",fontSize:"var(--text-xs)",color:theme.textMuted,textDecoration:"none",opacity:0.7 }}>Terms of Use</a>
          </div>
          <div style={{ fontFamily:"'DM Sans', sans-serif",fontSize:"var(--text-xs)",color:theme.textMuted,opacity:0.6 }}>See It First. — © 2026 SiteScope</div>
        </div>
      </div>
      </div>
    </div>

    {showDiagnostics && <AdminDiagnosticsPanel onClose={() => setShowDiagnostics(false)} />}
    </>
  );
}
